export interface BootstrapFetchRetryOptions {
  readonly maximumAttempts: number;
  readonly retryDelayMs: readonly number[];
  readonly origin: string;
  readonly onRetry?: (detail: Readonly<{
    attempt: number;
    maximumAttempts: number;
    url: string;
  }>) => void;
  readonly wait?: (delayMs: number, signal?: AbortSignal) => Promise<void>;
}

const DEFAULT_RETRY_DELAYS_MS = Object.freeze([250, 750, 1_500]);

function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

function isNetworkError(error: unknown): boolean {
  return error instanceof Error && error.name === "TypeError";
}

function waitForRetry(delayMs: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted === true) {
    return Promise.reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
  }
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", abort);
      resolve();
    };
    const abort = (): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      reject(signal?.reason ?? new DOMException("Aborted", "AbortError"));
    };
    const timeout = window.setTimeout(finish, delayMs);
    signal?.addEventListener("abort", abort, { once: true });
  });
}

function requestUrl(input: RequestInfo | URL): string {
  return input instanceof Request ? input.url : String(input);
}

function isRetryableRequest(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  origin: string,
): boolean {
  const method = (init?.method ?? (input instanceof Request ? input.method : "GET"))
    .toUpperCase();
  if (method !== "GET" && method !== "HEAD") return false;
  try {
    return new URL(requestUrl(input), window.location.href).origin === origin;
  } catch {
    return false;
  }
}

/**
 * Retry safe same-origin reads while the initial scene is fetching its
 * manifests and GLBs. One transient browser or network failure should not
 * strand the player at the startup screen.
 */
export function installBootstrapFetchRetry(
  options: Readonly<Partial<BootstrapFetchRetryOptions>> = {},
): () => void {
  const originalFetch = globalThis.fetch;
  const fetchOnce = originalFetch.bind(globalThis);
  const retryDelayMs = options.retryDelayMs ?? DEFAULT_RETRY_DELAYS_MS;
  const maximumAttempts = options.maximumAttempts ?? retryDelayMs.length + 1;
  const origin = options.origin ?? window.location.origin;
  const wait = options.wait ?? waitForRetry;

  if (!Number.isInteger(maximumAttempts) || maximumAttempts < 1) {
    throw new Error("Bootstrap fetch maximumAttempts must be a positive integer.");
  }

  const retryingFetch: typeof fetch = async (input, init) => {
    if (!isRetryableRequest(input, init, origin) || maximumAttempts === 1) {
      return fetchOnce(input, init);
    }

    const url = new URL(requestUrl(input), window.location.href).href;
    const signal =
      init?.signal ?? (input instanceof Request ? input.signal : undefined) ?? undefined;
    let lastNetworkError: unknown = null;

    for (let attempt = 1; attempt <= maximumAttempts; attempt += 1) {
      try {
        const response = await fetchOnce(input, init);
        if (!isRetryableStatus(response.status) || attempt === maximumAttempts) {
          return response;
        }
        await response.body?.cancel().catch(() => undefined);
        lastNetworkError = new Error(`HTTP ${response.status}`);
      } catch (error) {
        if (isAbortError(error) || !isNetworkError(error)) throw error;
        lastNetworkError = error;
        if (attempt === maximumAttempts) break;
      }

      const nextAttempt = attempt + 1;
      options.onRetry?.({
        attempt: nextAttempt,
        maximumAttempts,
        url,
      });
      await wait(
        retryDelayMs[Math.min(attempt - 1, retryDelayMs.length - 1)] ?? 0,
        signal,
      );
    }

    const reason = lastNetworkError instanceof Error
      ? lastNetworkError.message
      : "network request failed";
    throw new Error(
      `Could not load ${new URL(url).pathname} after ${maximumAttempts} attempts: ${reason}`,
    );
  };

  globalThis.fetch = retryingFetch;
  return () => {
    if (globalThis.fetch === retryingFetch) globalThis.fetch = originalFetch;
  };
}
